import { useState, useEffect, useRef, useCallback } from 'react'
import { useMutation, gql } from '@apollo/client'
import { Radar, RadarStatus, Importance, SaveRadarInput } from '../../gql/graphql'

type RadarDraft = {
  status: RadarStatus
  importance: Importance | null
  digest: string | null
  tags: string[] | null
  enterIntoForce: string[] | null
  assignedTo: number | null
}

const SAVE_RADAR = gql`
  mutation SaveRadar($input: SaveRadarInput!) {
    saveRadar(input: $input)
  }
`

const AUTO_SAVE_DELAY = 30_000

const toDraft = (value: Radar): RadarDraft => ({
  status: value.status,
  importance: value.importance ?? null,
  digest: value.digest ?? null,
  tags: (value.tags?.filter(Boolean) as string[]) ?? null,
  enterIntoForce: (value.enterIntoForce?.filter(Boolean) as string[]) ?? null,
  assignedTo: value.delegatedTo?.id ?? null,
})

export const useRadarItem = (initialValue: Radar) => {
  const [draft, setDraft] = useState<RadarDraft>(() => toDraft(initialValue))
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveRef = useRef<() => void>(() => {})

  const [saveRadarMutation] = useMutation<{ saveRadar: string }, { input: SaveRadarInput }>(
    SAVE_RADAR,
  )

  const save = useCallback(async () => {
    if (!isDirty) return
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setIsSaving(true)
    try {
      await saveRadarMutation({
        variables: {
          input: {
            id: initialValue.id,
            digest: draft.digest,
            status: draft.status,
            importance: draft.importance,
            tags: draft.tags,
            enterIntoForce: draft.enterIntoForce,
            assignedTo: draft.assignedTo,
          },
        },
      })
      setIsDirty(false)
    } finally {
      setIsSaving(false)
    }
  }, [draft, isDirty, initialValue.id, saveRadarMutation])

  // A ref mindig a legfrissebb save-et tartalmazza,
  // így a timer callback nem záródik be egy elavult verzióra.
  useEffect(() => {
    saveRef.current = save
  }, [save])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleChange = useCallback(<K extends keyof RadarDraft>(name: K, value: RadarDraft[K]) => {
    console.log(`handleChange: ${name} = ${value}`)

    setDraft((prev) => ({ ...prev, [name]: value }))
    setIsDirty(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => saveRef.current(), AUTO_SAVE_DELAY)
  }, [])

  return { draft, isDirty, isSaving, handleChange, save }
}
